import type { DebugMaterialMode, DebugMaterialRegistry } from '../debug/debug-materials'
import type { SchematicCameraController } from '../debug/schematic-view'
import { getDiscoveredSectors, type DiscoveredSector } from '../level/bsp-level'

export interface PauseMenuOptions {
  currentLevel: string
  onSelectLevel: (levelName: string) => void
  onResume: () => void
  debugRegistry: DebugMaterialRegistry
  schematicCamera: SchematicCameraController
}

export type SectorEntry = DiscoveredSector

export const KNOWN_SECTORS: SectorEntry[] = getDiscoveredSectors()

export class PauseMenu {
  readonly root: HTMLElement
  private readonly options: PauseMenuOptions
  private isVisible = false

  constructor(options: PauseMenuOptions) {
    this.options = options
    this.root = document.createElement('div')
    this.root.id = 'pause-menu-overlay'
    this.buildDOM()
    document.body.appendChild(this.root)
  }

  private buildDOM() {
    const sectors = getDiscoveredSectors()
    this.root.innerHTML = `
      <div class="pause-modal">
        <div class="pause-header">
          <div class="pause-title">
            <span class="pause-logo">ESOTERIA</span> // SECTOR DIRECTORY
          </div>
          <button id="pause-resume-btn" class="pause-btn resume-btn">RESUME MISSION [ESC]</button>
        </div>

        <div class="pause-body">
          <div class="pause-sidebar">
            <div class="pause-section-title">DIAGNOSTIC SHADERS</div>
            <div class="pause-button-group">
              <button class="shader-btn ${this.options.debugRegistry.mode === 'authentic' ? 'active' : ''}" data-mode="authentic">AUTHENTIC 1998</button>
              <button class="shader-btn ${this.options.debugRegistry.mode === 'type_colors' ? 'active' : ''}" data-mode="type_colors">TYPE_COLORS [F1]</button>
              <button class="shader-btn ${this.options.debugRegistry.mode === 'mesh_normals' ? 'active' : ''}" data-mode="mesh_normals">FACE NORMALS</button>
              <button class="shader-btn ${this.options.debugRegistry.mode === 'wireframe_normals' ? 'active' : ''}" data-mode="wireframe_normals">WIREFRAME</button>
            </div>

            <div class="pause-section-title" style="margin-top: 16px;">CAMERA PROJECTION</div>
            <div class="pause-button-group">
              <button id="pause-cam-btn" class="cam-btn ${this.options.schematicCamera.isSchematicActive ? 'active' : ''}">
                ${this.options.schematicCamera.isSchematicActive ? 'TOP-DOWN SCHEMATIC' : 'FIRST-PERSON [F2]'}
              </button>
            </div>

            <div class="pause-section-title" style="margin-top: 16px;">LOAD CUSTOM SECTOR</div>
            <div class="custom-sector-row">
              <input type="text" id="custom-sector-input" placeholder="e.g. eb08, q301, boiler" />
              <button id="custom-sector-btn">LOAD</button>
            </div>
          </div>

          <div class="pause-main">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
              <div class="pause-section-title" style="margin-bottom: 0;">SECTORS & RECONSTRUCTED LEVELS (${sectors.length} AVAILABLE)</div>
              <input type="text" id="sector-filter-input" placeholder="Filter sectors (e.g. EB, BOILER, MT, QUAD)..." style="background: rgba(15, 23, 42, 0.8); border: 1px solid rgba(255, 255, 255, 0.2); color: #00f0ff; font-family: inherit; font-size: 11px; padding: 6px 12px; border-radius: 4px; width: 240px; outline: none;" />
            </div>
            <div class="sector-grid" id="sector-grid">
              ${sectors.map((s) => {
                const isCurrent = s.id === this.options.currentLevel || (s.id === 'tutorial' && this.options.currentLevel === 'training')
                return `
                  <div class="sector-card ${isCurrent ? 'current' : ''}" data-level="${s.id}" data-search="${(s.name + ' ' + s.id + ' ' + s.description).toLowerCase()}">
                    <div class="sector-card-header">
                      <span class="sector-name">${s.name}</span>
                      ${s.badge ? `<span class="sector-badge">${s.badge}</span>` : ''}
                    </div>
                    <div class="sector-desc">${s.description}</div>
                    <div class="sector-meta">
                      <span>Area: <b>${s.area}</b></span>
                      <span>Verts: <b>${s.verts}</b></span>
                      <span>Objects: <b>${s.objects}</b></span>
                    </div>
                    <button class="load-sector-btn ${isCurrent ? 'active' : ''}">
                      ${isCurrent ? '● CURRENT SECTOR' : 'LOAD SECTOR →'}
                    </button>
                  </div>
                `
              }).join('')}
            </div>
          </div>
        </div>
      </div>
    `

    // Event listeners
    this.root.querySelector('#pause-resume-btn')?.addEventListener('click', () => {
      this.options.onResume()
    })

    // Shader buttons
    this.root.querySelectorAll('.shader-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const mode = (e.currentTarget as HTMLElement).getAttribute('data-mode') as DebugMaterialMode
        this.options.debugRegistry.setMode(mode)
        this.root.querySelectorAll('.shader-btn').forEach((b) => b.classList.remove('active'))
        ;(e.currentTarget as HTMLElement).classList.add('active')
      })
    })

    // Camera toggle
    const camBtn = this.root.querySelector('#pause-cam-btn') as HTMLButtonElement
    camBtn?.addEventListener('click', () => {
      this.options.schematicCamera.toggleSchematic()
      camBtn.textContent = this.options.schematicCamera.isSchematicActive ? 'TOP-DOWN SCHEMATIC' : 'FIRST-PERSON [F2]'
      camBtn.classList.toggle('active', this.options.schematicCamera.isSchematicActive)
    })

    // Sector card selection
    this.root.querySelectorAll('.sector-card').forEach((card) => {
      card.addEventListener('click', (e) => {
        const levelId = (e.currentTarget as HTMLElement).getAttribute('data-level')
        if (levelId) {
          this.options.onSelectLevel(levelId)
        }
      })
    })

    this.root.querySelectorAll('.load-sector-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        const card = (e.currentTarget as HTMLElement).closest('.sector-card')
        const levelId = card?.getAttribute('data-level')
        if (levelId) {
          this.options.onSelectLevel(levelId)
        }
      })
    })

    // Instant sector search filter
    const filterInput = this.root.querySelector('#sector-filter-input') as HTMLInputElement
    filterInput?.addEventListener('input', () => {
      const q = filterInput.value.trim().toLowerCase()
      this.root.querySelectorAll('.sector-card').forEach((card) => {
        const searchStr = (card as HTMLElement).getAttribute('data-search') || ''
        const matches = !q || searchStr.includes(q)
        ;(card as HTMLElement).style.display = matches ? 'flex' : 'none'
      })
    })

    // Custom sector submit
    const customInput = this.root.querySelector('#custom-sector-input') as HTMLInputElement
    const customBtn = this.root.querySelector('#custom-sector-btn') as HTMLButtonElement
    const handleCustomSubmit = () => {
      const val = customInput.value.trim().toLowerCase()
      if (val) {
        this.options.onSelectLevel(val)
      }
    }
    customBtn?.addEventListener('click', handleCustomSubmit)
    customInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') handleCustomSubmit()
    })
  }

  show() {
    this.root.classList.add('visible')
    this.isVisible = true
  }

  hide() {
    this.root.classList.remove('visible')
    this.isVisible = false
  }

  toggle(): boolean {
    if (this.isVisible) {
      this.hide()
    } else {
      this.show()
    }
    return this.isVisible
  }
}
